/**
 * Logic Primitive Operations
 *
 * Fundamental operations for comparisons, boolean logic, and conditionals.
 */

import { defineOperation, PrimitiveOperation } from './operations.model';

// ------------------------------------------------
// COMPARISON OPERATIONS
// ------------------------------------------------

export const eq = defineOperation<unknown, boolean>({
  id: 'op.eq',
  name: 'Equal',
  symbol: '=',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a === b,
  latex: '{{a}} = {{b}}',
  description: 'Tests if two values are equal',
  example: '$5 = 5 \\Rightarrow \\text{true}$',
});

export const neq = defineOperation<unknown, boolean>({
  id: 'op.neq',
  name: 'Not Equal',
  symbol: '≠',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a !== b,
  latex: '{{a}} \\neq {{b}}',
  description: 'Tests if two values are not equal',
  example: '$3 \\neq 5 \\Rightarrow \\text{true}$',
});

export const lt = defineOperation<number, boolean>({
  id: 'op.lt',
  name: 'Less Than',
  symbol: '<',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a < b,
  latex: '{{a}} < {{b}}',
  description: 'Tests if the first value is less than the second',
  example: '$3 < 5 \\Rightarrow \\text{true}$',
});

export const gt = defineOperation<number, boolean>({
  id: 'op.gt',
  name: 'Greater Than',
  symbol: '>',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a > b,
  latex: '{{a}} > {{b}}',
  description: 'Tests if the first value is greater than the second',
  example: '$7 > 3 \\Rightarrow \\text{true}$',
});

export const lte = defineOperation<number, boolean>({
  id: 'op.lte',
  name: 'Less Than or Equal',
  symbol: '≤',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a <= b,
  latex: '{{a}} \\leq {{b}}',
  description: 'Tests if the first value is less than or equal to the second',
  example: '$5 \\leq 5 \\Rightarrow \\text{true}$',
});

export const gte = defineOperation<number, boolean>({
  id: 'op.gte',
  name: 'Greater Than or Equal',
  symbol: '≥',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a >= b,
  latex: '{{a}} \\geq {{b}}',
  description: 'Tests if the first value is greater than or equal to the second',
  example: '$5 \\geq 3 \\Rightarrow \\text{true}$',
});

// ------------------------------------------------
// BOOLEAN OPERATIONS
// ------------------------------------------------

export const and = defineOperation<boolean, boolean>({
  id: 'op.and',
  name: 'Logical AND',
  symbol: '∧',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a && b,
  latex: '{{a}} \\land {{b}}',
  description: 'Returns true only if both operands are true',
  example: '$\\text{true} \\land \\text{false} \\Rightarrow \\text{false}$',
});

export const or = defineOperation<boolean, boolean>({
  id: 'op.or',
  name: 'Logical OR',
  symbol: '∨',
  category: 'logic',
  arity: 2,
  execute: (a, b) => a || b,
  latex: '{{a}} \\lor {{b}}',
  description: 'Returns true if at least one operand is true',
  example: '$\\text{true} \\lor \\text{false} \\Rightarrow \\text{true}$',
});

export const not = defineOperation<boolean, boolean>({
  id: 'op.not',
  name: 'Logical NOT',
  symbol: '¬',
  category: 'logic',
  arity: 1,
  execute: (a) => !a,
  latex: '\\neg {{a}}',
  description: 'Inverts a boolean value',
  example: '$\\neg \\text{true} \\Rightarrow \\text{false}$',
});

// ------------------------------------------------
// CONDITIONAL OPERATION
// ------------------------------------------------

export const ifOp = defineOperation<unknown, unknown>({
  id: 'op.if',
  name: 'Conditional',
  symbol: '?:',
  category: 'logic',
  arity: 3,
  execute: (condition, thenValue, elseValue) => (condition ? thenValue : elseValue),
  latex: '\\text{if } {{a}} \\text{ then } {{b}} \\text{ else } {{c}}',
  description: 'Returns one of two values based on a condition',
  example: '$\\text{if } x > 0 \\text{ then } 1 \\text{ else } -1$',
});

// ------------------------------------------------
// EXPORT ALL LOGIC OPERATIONS
// ------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logicOperations: Array<PrimitiveOperation<any, any>> = [eq, neq, lt, gt, lte, gte, and, or, not, ifOp];
